import { useCallback, useMemo, useState } from "react"
import { useSearchParams } from "react-router"

import {
  inclusiveIntervalEnd,
  toExclusiveInterval,
  toInclusiveInterval,
} from "@/utils/date"
import { useDateRange } from "@/utils/date/use-date-range"
import { DateTime, Interval } from "luxon"

import {
  INTERVAL_GRANULARITY_PARAM,
  type IntervalGranularity,
  defaultGranularity,
  defaultIntervalDateRange,
  defaultIntervalInclusiveEnd,
  intervalDateRangeBounds,
  isIntervalGranularity,
} from "./interval-data-selection"

interface UseIntervalDataSelectionOptions {
  latestStartDatetime: string | null
  scopeIsPending: boolean
  siteId: string
  timeZone: string
}

interface UseIntervalDataSelectionResult {
  dateRange: Interval
  granularity: IntervalGranularity
  maxDate: DateTime | null
  minDate: DateTime | null
  setDateRange: (value: Interval) => void
  setGranularity: (nextGranularity: IntervalGranularity) => void
}

const granularitySearchParams = (
  prev: URLSearchParams,
  granularity: IntervalGranularity
): URLSearchParams => {
  const params = new URLSearchParams(prev)
  params.set(INTERVAL_GRANULARITY_PARAM, granularity)
  return params
}

export const useIntervalDataSelection = ({
  latestStartDatetime,
  scopeIsPending,
  siteId,
  timeZone,
}: UseIntervalDataSelectionOptions): UseIntervalDataSelectionResult => {
  const [searchParams, setSearchParams] = useSearchParams()
  const [bounds] = useState(() =>
    intervalDateRangeBounds(defaultIntervalInclusiveEnd({ timeZone }))
  )
  const [pendingBounds] = useState(() =>
    Interval.invalid("pending interval date default")
  )
  const fallbackEnd = useMemo(
    () =>
      defaultIntervalInclusiveEnd({
        latestStartDatetime,
        now: inclusiveIntervalEnd(bounds) ?? DateTime.now(),
        timeZone,
      }),
    [bounds, latestStartDatetime, timeZone]
  )
  const fallbackDateRange = useMemo(
    () => toInclusiveInterval(defaultIntervalDateRange(fallbackEnd)),
    [fallbackEnd]
  )
  const { dateRange: urlDateRange, setDateRange: setUrlDateRange } =
    useDateRange({
      bounds: scopeIsPending ? pendingBounds : bounds,
      fallbackDateRange,
      id: `site-interval-data-${siteId}`,
    })
  const dateRange = useMemo(
    () => toExclusiveInterval(urlDateRange),
    [urlDateRange]
  )
  const setDateRange = useCallback(
    (value: Interval) => {
      setUrlDateRange(toInclusiveInterval(value))
    },
    [setUrlDateRange]
  )

  const granularityParam = searchParams.get(INTERVAL_GRANULARITY_PARAM)
  const granularity = isIntervalGranularity(granularityParam)
    ? granularityParam
    : defaultGranularity()

  const setGranularity = (nextGranularity: IntervalGranularity) => {
    setSearchParams((prev) => granularitySearchParams(prev, nextGranularity), {
      replace: true,
    })
  }

  return {
    dateRange,
    granularity,
    maxDate: inclusiveIntervalEnd(bounds),
    minDate: bounds.start,
    setDateRange,
    setGranularity,
  }
}
